import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { roomsApi } from '../api'
import type { Room } from '../api/types'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useRoomSessionApi } from '../context/RoomSessionContext'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Toggle } from '../components/ui/Toggle'
import { Dialog } from '../components/ui/Dialog'
import { Spinner } from '../components/ui/Spinner'
import { FeedPanel } from '../components/FeedPanel'
import { ApiError } from '../api/client'

export function HomePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const { active, leaveSession } = useRoomSessionApi()
  const [rooms, setRooms] = useState<Room[] | null>(null)
  const [creating, setCreating] = useState(false)
  const [joinDialog, setJoinDialog] = useState<Room | null>(null)
  const [switchPrompt, setSwitchPrompt] = useState<Room | null>(null)

  useEffect(() => {
    void loadRooms()
    const t = window.setInterval(() => { void loadRooms() }, 10000)
    return () => window.clearInterval(t)
  }, [])

  async function loadRooms() {
    try {
      const { rooms } = await roomsApi.list()
      setRooms(rooms)
    } catch {
      toast.push('Räume konnten nicht geladen werden', 'error')
    }
  }

  async function handleDelete(room: Room) {
    if (!window.confirm(`"${room.name}" wirklich löschen?`)) return
    try {
      // If we're deleting the room that's currently active, end the session.
      if (active && active.slug === room.slug) leaveSession()
      await roomsApi.remove(room.id)
      toast.push('Raum gelöscht', 'success')
      void loadRooms()
    } catch {
      toast.push('Löschen fehlgeschlagen', 'error')
    }
  }

  async function openRoom(room: Room) {
    // Already in this room — just navigate back; no extra join required.
    if (active && active.slug === room.slug) {
      navigate(`/rooms/${encodeURIComponent(room.slug)}`)
      return
    }
    // In a different room — confirm before leaving the active session.
    if (active && active.slug !== room.slug) {
      setSwitchPrompt(room)
      return
    }
    try {
      await roomsApi.join(room.slug)
      navigate(`/rooms/${encodeURIComponent(room.slug)}`)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setJoinDialog(room)
        return
      }
      toast.push('Beitritt fehlgeschlagen', 'error')
    }
  }

  return (
    <div className="page page-wide">
      <div className="page-head">
        <div>
          <h1>Räume</h1>
          <p className="muted">Starte oder tritt einem Raum bei.</p>
        </div>
        <Button onClick={() => setCreating(true)}>+ Neuer Raum</Button>
      </div>

      <div className="home-layout">
      <div className="home-main">
      {rooms === null ? (
        <Spinner />
      ) : rooms.length === 0 ? (
        <div className="empty">
          <p>Noch keine Räume. Erstelle den ersten für deine Familie.</p>
        </div>
      ) : (
        <ul className="room-grid">
          {rooms.map((r) => (
            <li key={r.id} className={`room-card ${active?.slug === r.slug ? 'is-active' : ''}`}>
              <div className="room-card-body">
                <h3>{r.name}</h3>
                <div className="room-meta">
                  {r.isPrivate ? <span className="tag">Privat</span> : null}
                  {r.lobbyEnabled ? <span className="tag">Lobby</span> : null}
                  {active?.slug === r.slug ? (
                    <span className="tag tag-accent">Aktive Sitzung</span>
                  ) : null}
                  {r.participantCount != null && r.participantCount > 0 ? (
                    <span className="tag tag-live">
                      {r.participantCount === 1
                        ? '1 Person aktiv'
                        : `${r.participantCount} Personen aktiv`}
                    </span>
                  ) : null}
                </div>
                <div className="room-url">/rooms/{r.slug}</div>
              </div>
              <div className="room-card-actions">
                <Button size="sm" onClick={() => openRoom(r)}>
                  {active?.slug === r.slug ? 'Zurück' : 'Beitreten'}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    const link = `${window.location.origin}/rooms/${r.slug}`
                    navigator.clipboard?.writeText(link)
                    toast.push('Einladungslink kopiert', 'success')
                  }}
                >
                  Link kopieren
                </Button>
                {user && (user.id === r.ownerId || user.role === 'ADMIN') ? (
                  <Button size="sm" variant="danger" onClick={() => handleDelete(r)}>
                    Löschen
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      </div>
      <FeedPanel />
      </div>

      <CreateRoomDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false)
          void loadRooms()
        }}
      />

      <JoinPasswordDialog
        room={joinDialog}
        onClose={() => setJoinDialog(null)}
        onJoined={(slug) => {
          setJoinDialog(null)
          navigate(`/rooms/${encodeURIComponent(slug)}`)
        }}
      />

      <Dialog
        open={!!switchPrompt}
        onClose={() => setSwitchPrompt(null)}
        title="In anderen Raum wechseln?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSwitchPrompt(null)}>
              Abbrechen
            </Button>
            <Button
              onClick={async () => {
                const target = switchPrompt
                if (!target) return
                leaveSession()
                setSwitchPrompt(null)
                try {
                  await roomsApi.join(target.slug)
                  navigate(`/rooms/${encodeURIComponent(target.slug)}`)
                } catch (err) {
                  if (err instanceof ApiError && err.status === 403) {
                    setJoinDialog(target)
                    return
                  }
                  toast.push('Beitritt fehlgeschlagen', 'error')
                }
              }}
            >
              Wechseln
            </Button>
          </>
        }
      >
        <p>
          Du bist gerade im Raum „{active?.room.name ?? ''}". Wenn du jetzt „
          {switchPrompt?.name ?? ''}" beitrittst, wird die laufende Verbindung beendet.
        </p>
      </Dialog>
    </div>
  )
}

function CreateRoomDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: () => void
}) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [password, setPassword] = useState('')
  const [lobbyEnabled, setLobbyEnabled] = useState(false)
  const [chatEnabled, setChatEnabled] = useState(true)
  const [screenShareEnabled, setScreenShareEnabled] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  async function submit() {
    setSubmitting(true)
    try {
      await roomsApi.create({
        name,
        isPrivate,
        password: password || undefined,
        lobbyEnabled,
        chatEnabled,
        screenShareEnabled,
      })
      toast.push('Raum erstellt', 'success')
      setName('')
      setPassword('')
      setIsPrivate(false)
      onCreated()
    } catch {
      toast.push('Erstellen fehlgeschlagen', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Neuen Raum erstellen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={submit} loading={submitting} disabled={!name.trim()}>
            Erstellen
          </Button>
        </>
      }
    >
      <div className="stack">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        <Toggle label="Privat" checked={isPrivate} onCheckedChange={setIsPrivate} />
        <Input
          label="Passwort (optional)"
          type="password"
          hint="Mindestens 4 Zeichen"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Toggle label="Lobby aktivieren" checked={lobbyEnabled} onCheckedChange={setLobbyEnabled} />
        <Toggle label="Chat aktivieren" checked={chatEnabled} onCheckedChange={setChatEnabled} />
        <Toggle
          label="Bildschirmfreigabe aktivieren"
          checked={screenShareEnabled}
          onCheckedChange={setScreenShareEnabled}
        />
      </div>
    </Dialog>
  )
}

function JoinPasswordDialog({
  room,
  onClose,
  onJoined,
}: {
  room: Room | null
  onClose: () => void
  onJoined: (slug: string) => void
}) {
  const toast = useToast()
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function submit() {
    if (!room) return
    setSubmitting(true)
    try {
      await roomsApi.join(room.slug, password)
      onJoined(room.slug)
    } catch {
      toast.push('Passwort ungültig', 'error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog
      open={!!room}
      onClose={onClose}
      title="Passwort erforderlich"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={submit} loading={submitting}>
            Beitreten
          </Button>
        </>
      }
    >
      <Input
        type="password"
        label="Raum-Passwort"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
    </Dialog>
  )
}
